// Minimal, dependency-free HTML sanitiser used before rendering policy body HTML.
// The backend already sanitises on save, but the public signing page renders
// content fetched over the network, so we defensively strip anything unsafe.
const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote',
  'a', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
])

const ALLOWED_ATTRS = {
  a: new Set(['href', 'title', 'target', 'rel']),
}

function isSafeHref(href) {
  const value = (href ?? '').trim().toLowerCase()
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('mailto:') ||
    value.startsWith('/') ||
    value.startsWith('#')
  )
}

// Returns a sanitised HTML string safe for dangerouslySetInnerHTML.
export function sanitizeHtml(dirty) {
  if (!dirty) return ''

  const template = document.createElement('template')
  template.innerHTML = String(dirty)

  const walk = (node) => {
    const children = Array.from(node.childNodes)

    children.forEach((child) => {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
        return
      }

      if (child.nodeType !== Node.ELEMENT_NODE) return

      const tag = child.tagName.toLowerCase()

      if (!ALLOWED_TAGS.has(tag)) {
        // Unwrap: keep children (text) but drop the disallowed element itself.
        walk(child)
        const fragment = document.createDocumentFragment()
        while (child.firstChild) fragment.appendChild(child.firstChild)
        child.replaceWith(fragment)
        return
      }

      // Strip every attribute except those explicitly allowed for the tag.
      const allowedForTag = ALLOWED_ATTRS[tag]
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase()
        const keep = allowedForTag?.has(name) && !(name === 'href' && !isSafeHref(attr.value))
        if (!keep) child.removeAttribute(attr.name)
      })

      // Force links to open safely in a new tab.
      if (tag === 'a') {
        child.setAttribute('rel', 'noopener noreferrer')
        child.setAttribute('target', '_blank')
      }

      walk(child)
    })
  }

  walk(template.content)
  return template.innerHTML
}

export default sanitizeHtml
